import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { GraduationCap, Zap, FolderGit2, XCircle, Clock, RefreshCw, History, Pencil, Check, X } from 'lucide-react';
import { API_BASE, getAdminToken, formatDate } from './adminUtils';

const statusColors = {
  active: 'bg-emerald-500/20 text-emerald-300',
  expired: 'bg-amber-500/20 text-amber-300',
  revoked: 'bg-red-500/20 text-red-300',
};

const EditableReason = ({ value, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(value || '');

  if (!editing) {
    return (
      <span className="flex items-center gap-1 group">
        <span className="block truncate cursor-help" title={value || '-'}>{value || '-'}</span>
        <button onClick={() => { setText(value || ''); setEditing(true); }}
          className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-teal-400 transition-opacity flex-shrink-0">
          <Pencil className="w-3 h-3" />
        </button>
      </span>
    );
  }

  return (
    <span className="flex items-center gap-1">
      <input type="text" value={text} onChange={e => setText(e.target.value)} autoFocus
        className="bg-white/10 border border-teal-500/50 rounded px-2 py-0.5 text-white text-xs w-full min-w-[120px]"
        onKeyDown={e => { if (e.key === 'Enter') { onSave(text); setEditing(false); } if (e.key === 'Escape') setEditing(false); }} />
      <button onClick={() => { onSave(text); setEditing(false); }} className="text-green-400 hover:text-green-300 flex-shrink-0"><Check className="w-3.5 h-3.5" /></button>
      <button onClick={() => setEditing(false)} className="text-red-400 hover:text-red-300 flex-shrink-0"><X className="w-3.5 h-3.5" /></button>
    </span>
  );
};

export const EnrollTab = ({ onStatsRefresh }) => {
  const [enrollCourses, setEnrollCourses] = useState([]);
  const [enrollEmail, setEnrollEmail] = useState('');
  const [enrollCourse, setEnrollCourse] = useState('');
  const [enrollReason, setEnrollReason] = useState('');
  const [enrollExpires, setEnrollExpires] = useState('');
  const [enrolling, setEnrolling] = useState(false);

  const [revokeEmail, setRevokeEmail] = useState('');
  const [revokeCourse, setRevokeCourse] = useState('');
  const [revoking, setRevoking] = useState(false);

  const [adminProjects, setAdminProjects] = useState([]);
  const [grantEmail, setGrantEmail] = useState('');
  const [grantProject, setGrantProject] = useState('');
  const [grantReason, setGrantReason] = useState('');
  const [grantExpires, setGrantExpires] = useState('');
  const [granting, setGranting] = useState(false);

  const [revokeProjectEmail, setRevokeProjectEmail] = useState('');
  const [revokeProject, setRevokeProject] = useState('');
  const [revokingProject, setRevokingProject] = useState(false);

  const [grantHistory, setGrantHistory] = useState([]);
  const [historySummary, setHistorySummary] = useState(null);
  const [historyFilter, setHistoryFilter] = useState('all');
  const [historyTypeFilter, setHistoryTypeFilter] = useState('all');
  const [historyLoading, setHistoryLoading] = useState(false);

  const updateReason = async (grantId, newReason) => {
    const token = getAdminToken(); if (!token) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/grants/${grantId}/reason`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: newReason })
      });
      if (res.ok) { toast.success('Reason updated'); fetchGrantHistory(); }
      else toast.error('Update failed');
    } catch { toast.error('Error'); }
  };

  const fetchEnrollCourses = useCallback(async () => {
    const token = getAdminToken(); if (!token) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/courses`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        // Map courses data to {id, name} format for dropdown
        const courseList = (data.courses || []).map(c => ({ id: c.id, name: c.name || c.id }));
        setEnrollCourses(courseList);
      }
    } catch (e) { /* silent */ }
  }, []);

  const fetchAdminProjects = useCallback(async () => {
    const token = getAdminToken(); if (!token) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/projects`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setAdminProjects((await res.json()).projects);
    } catch (e) { /* silent */ }
  }, []);

  const fetchGrantHistory = useCallback(async () => {
    const token = getAdminToken(); if (!token) return;
    setHistoryLoading(true);
    try {
      const params = new URLSearchParams();
      if (historyFilter !== 'all') params.append('status_filter', historyFilter);
      if (historyTypeFilter !== 'all') params.append('type_filter', historyTypeFilter);
      const res = await fetch(`${API_BASE}/api/admin/grant-history?${params}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const d = await res.json();
        setGrantHistory(d.grants);
        setHistorySummary(d.summary);
      }
    } catch (e) { /* silent */ }
    finally { setHistoryLoading(false); }
  }, [historyFilter, historyTypeFilter]);

  useEffect(() => { fetchEnrollCourses(); fetchAdminProjects(); fetchGrantHistory(); }, []);
  useEffect(() => { fetchGrantHistory(); }, [historyFilter, historyTypeFilter]);

  const handleManualEnroll = async () => {
    if (!enrollEmail || !enrollCourse) { toast.error('Enter email and select course'); return; }
    const token = getAdminToken(); if (!token) return;
    setEnrolling(true);
    try {
      const body = { user_email: enrollEmail, course_id: enrollCourse, reason: enrollReason || 'Manual' };
      if (enrollExpires) body.expires_at = new Date(enrollExpires).toISOString();
      const res = await fetch(`${API_BASE}/api/admin/enroll-manual`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Failed');
      toast.success(d.message); setEnrollEmail(''); setEnrollCourse(''); setEnrollReason(''); setEnrollExpires(''); onStatsRefresh?.(); fetchGrantHistory();
    } catch (e) { toast.error(e.message); }
    finally { setEnrolling(false); }
  };

  const handleRevokeEnrollment = async () => {
    if (!revokeEmail || !revokeCourse) { toast.error('Enter email and select course'); return; }
    if (!window.confirm(`Revoke ${revokeEmail}'s access to ${revokeCourse}?`)) return;
    const token = getAdminToken(); if (!token) return;
    setRevoking(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/revoke-enrollment`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ user_email: revokeEmail, item_id: revokeCourse })
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Failed');
      toast.success(d.message); setRevokeEmail(''); setRevokeCourse(''); onStatsRefresh?.(); fetchGrantHistory();
    } catch (e) { toast.error(e.message); }
    finally { setRevoking(false); }
  };

  const handleGrantProject = async () => {
    if (!grantEmail || !grantProject) { toast.error('Enter email and select project'); return; }
    const token = getAdminToken(); if (!token) return;
    setGranting(true);
    try {
      const body = { user_email: grantEmail, project_id: grantProject, reason: grantReason || 'Manual grant' };
      if (grantExpires) body.expires_at = new Date(grantExpires).toISOString();
      const res = await fetch(`${API_BASE}/api/admin/grant-project`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Failed');
      toast.success(d.message); setGrantEmail(''); setGrantProject(''); setGrantReason(''); setGrantExpires(''); fetchGrantHistory();
    } catch (e) { toast.error(e.message); }
    finally { setGranting(false); }
  };

  const handleRevokeProject = async () => {
    if (!revokeProjectEmail || !revokeProject) { toast.error('Enter email and select project'); return; }
    if (!window.confirm(`Revoke ${revokeProjectEmail}'s project access?`)) return;
    const token = getAdminToken(); if (!token) return;
    setRevokingProject(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/revoke-project`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ user_email: revokeProjectEmail, item_id: revokeProject })
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Failed');
      toast.success(d.message); setRevokeProjectEmail(''); setRevokeProject(''); fetchGrantHistory();
    } catch (e) { toast.error(e.message); }
    finally { setRevokingProject(false); }
  };

  return (
    <div className="space-y-4">
      {/* Course Enrollment */}
      <Card className="bg-white/5 border-white/10 backdrop-blur">
        <CardHeader><CardTitle className="text-white flex items-center gap-2"><GraduationCap className="w-5 h-5 text-amber-400" />Course Enrollment</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Enroll */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-emerald-400">Enroll User</h3>
              <div><label className="text-xs text-slate-400 block mb-1">User Email</label><Input placeholder="Enter email" value={enrollEmail} onChange={e => setEnrollEmail(e.target.value)} className="bg-white/10 border-white/20 text-white" data-testid="enroll-email" /></div>
              <div><label className="text-xs text-slate-400 block mb-1">Course</label>
                <select value={enrollCourse} onChange={e => setEnrollCourse(e.target.value)} className="w-full bg-white/10 border border-white/20 text-white rounded-md px-3 py-2 text-sm" data-testid="enroll-course">
                  <option value="" className="bg-slate-800">-- Select Course --</option>
                  {enrollCourses.map(c => <option key={c.id} value={c.id} className="bg-slate-800">{c.name}</option>)}
                </select>
              </div>
              <div><label className="text-xs text-slate-400 block mb-1">Access Valid Till <span className="text-slate-600">(optional, empty = lifetime)</span></label>
                <Input type="date" value={enrollExpires} onChange={e => setEnrollExpires(e.target.value)} className="bg-white/10 border-white/20 text-white" data-testid="enroll-expires" min={new Date().toISOString().split('T')[0]} />
              </div>
              <div><label className="text-xs text-slate-400 block mb-1">Reason</label><Input placeholder="Optional" value={enrollReason} onChange={e => setEnrollReason(e.target.value)} className="bg-white/10 border-white/20 text-white" /></div>
              <Button onClick={handleManualEnroll} disabled={enrolling || !enrollEmail || !enrollCourse} className="w-full bg-gradient-to-r from-amber-500 to-orange-500 text-white" data-testid="enroll-btn">
                <Zap className="w-4 h-4 mr-2" />{enrolling ? 'Enrolling...' : 'Enroll'}
              </Button>
            </div>
            {/* Revoke */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-red-400">Revoke Enrollment</h3>
              <div><label className="text-xs text-slate-400 block mb-1">User Email</label><Input placeholder="Enter email" value={revokeEmail} onChange={e => setRevokeEmail(e.target.value)} className="bg-white/10 border-white/20 text-white" data-testid="revoke-enroll-email" /></div>
              <div><label className="text-xs text-slate-400 block mb-1">Course</label>
                <select value={revokeCourse} onChange={e => setRevokeCourse(e.target.value)} className="w-full bg-white/10 border border-white/20 text-white rounded-md px-3 py-2 text-sm" data-testid="revoke-enroll-course">
                  <option value="" className="bg-slate-800">-- Select Course --</option>
                  {enrollCourses.map(c => <option key={c.id} value={c.id} className="bg-slate-800">{c.name}</option>)}
                </select>
              </div>
              <Button onClick={handleRevokeEnrollment} disabled={revoking || !revokeEmail || !revokeCourse} className="w-full bg-gradient-to-r from-red-500 to-red-600 text-white" data-testid="revoke-enroll-btn">
                <XCircle className="w-4 h-4 mr-2" />{revoking ? 'Revoking...' : 'Revoke Access'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Project Access */}
      <Card className="bg-white/5 border-white/10 backdrop-blur">
        <CardHeader><CardTitle className="text-white flex items-center gap-2"><FolderGit2 className="w-5 h-5 text-orange-400" />Project Access</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Grant */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-emerald-400">Grant Access</h3>
              <div><label className="text-xs text-slate-400 block mb-1">User Email</label><Input placeholder="Enter email" value={grantEmail} onChange={e => setGrantEmail(e.target.value)} className="bg-white/10 border-white/20 text-white" data-testid="grant-project-email" /></div>
              <div><label className="text-xs text-slate-400 block mb-1">Project</label>
                <select value={grantProject} onChange={e => setGrantProject(e.target.value)} className="w-full bg-white/10 border border-white/20 text-white rounded-md px-3 py-2 text-sm" data-testid="grant-project-select">
                  <option value="" className="bg-slate-800">-- Select Project --</option>
                  {adminProjects.filter(p => p.is_enabled).map(p => <option key={p.id} value={p.id} className="bg-slate-800">{p.title} ({p.category})</option>)}
                </select>
              </div>
              <div><label className="text-xs text-slate-400 block mb-1">Access Valid Till <span className="text-slate-600">(optional, empty = lifetime)</span></label>
                <Input type="date" value={grantExpires} onChange={e => setGrantExpires(e.target.value)} className="bg-white/10 border-white/20 text-white" data-testid="grant-expires" min={new Date().toISOString().split('T')[0]} />
              </div>
              <div><label className="text-xs text-slate-400 block mb-1">Reason</label><Input placeholder="Optional (e.g. Demo, Free access)" value={grantReason} onChange={e => setGrantReason(e.target.value)} className="bg-white/10 border-white/20 text-white" /></div>
              <Button onClick={handleGrantProject} disabled={granting || !grantEmail || !grantProject} className="w-full bg-gradient-to-r from-orange-500 to-amber-500 text-white" data-testid="grant-project-btn">
                <FolderGit2 className="w-4 h-4 mr-2" />{granting ? 'Granting...' : 'Grant Access'}
              </Button>
            </div>
            {/* Revoke */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-red-400">Revoke Access</h3>
              <div><label className="text-xs text-slate-400 block mb-1">User Email</label><Input placeholder="Enter email" value={revokeProjectEmail} onChange={e => setRevokeProjectEmail(e.target.value)} className="bg-white/10 border-white/20 text-white" data-testid="revoke-project-email" /></div>
              <div><label className="text-xs text-slate-400 block mb-1">Project</label>
                <select value={revokeProject} onChange={e => setRevokeProject(e.target.value)} className="w-full bg-white/10 border border-white/20 text-white rounded-md px-3 py-2 text-sm" data-testid="revoke-project-select">
                  <option value="" className="bg-slate-800">-- Select Project --</option>
                  {adminProjects.filter(p => p.is_enabled).map(p => <option key={p.id} value={p.id} className="bg-slate-800">{p.title} ({p.category})</option>)}
                </select>
              </div>
              <Button onClick={handleRevokeProject} disabled={revokingProject || !revokeProjectEmail || !revokeProject} className="w-full bg-gradient-to-r from-red-500 to-red-600 text-white" data-testid="revoke-project-btn">
                <XCircle className="w-4 h-4 mr-2" />{revokingProject ? 'Revoking...' : 'Revoke Access'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Grant History */}
      <Card className="bg-white/5 border-white/10 backdrop-blur">
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            <CardTitle className="text-white flex items-center gap-2">
              <History className="w-5 h-5 text-blue-400" />Grant History
              {historySummary && (
                <div className="flex gap-2 ml-2">
                  <Badge className="bg-emerald-500/20 text-emerald-300">{historySummary.active} Active</Badge>
                  <Badge className="bg-amber-500/20 text-amber-300">{historySummary.expired} Expired</Badge>
                  <Badge className="bg-red-500/20 text-red-300">{historySummary.revoked} Revoked</Badge>
                </div>
              )}
            </CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              {['all', 'active', 'expired', 'revoked'].map(f => (
                <button key={f} onClick={() => setHistoryFilter(f)} className={`px-3 py-1 rounded-lg text-xs font-medium transition-all ${historyFilter === f ? 'bg-blue-500 text-white' : 'bg-white/10 text-slate-400 hover:bg-white/20'}`} data-testid={`history-filter-${f}`}>
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
              <span className="text-slate-600">|</span>
              {['all', 'course', 'project'].map(f => (
                <button key={f} onClick={() => setHistoryTypeFilter(f)} className={`px-3 py-1 rounded-lg text-xs font-medium transition-all ${historyTypeFilter === f ? 'bg-purple-500 text-white' : 'bg-white/10 text-slate-400 hover:bg-white/20'}`}>
                  {f === 'all' ? 'All Types' : f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
              <Button size="sm" variant="outline" onClick={fetchGrantHistory} className="border-white/20 text-white" data-testid="refresh-history"><RefreshCw className="w-3 h-3" /></Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {historyLoading ? (
            <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 text-blue-500 animate-spin" /></div>
          ) : grantHistory.length === 0 ? (
            <p className="text-slate-400 text-center py-6">No manual grants found</p>
          ) : (
            <div className="rounded-xl border border-white/10 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10">
                    <TableHead className="text-slate-300">User</TableHead>
                    <TableHead className="text-slate-300">Type</TableHead>
                    <TableHead className="text-slate-300">Item</TableHead>
                    <TableHead className="text-slate-300">Reason</TableHead>
                    <TableHead className="text-slate-300">Granted</TableHead>
                    <TableHead className="text-slate-300">Expires</TableHead>
                    <TableHead className="text-slate-300">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {grantHistory.map((g, i) => (
                    <TableRow key={i} className="border-white/10 hover:bg-white/5">
                      <TableCell className="text-white text-sm">{g.user_email}</TableCell>
                      <TableCell>
                        <Badge className={g.type === 'course' ? 'bg-amber-500/20 text-amber-300' : 'bg-orange-500/20 text-orange-300'}>
                          {g.type === 'course' ? 'Course' : 'Project'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-300 text-sm max-w-[200px]"><span className="block truncate" title={g.item_name}>{g.item_name}</span></TableCell>
                      <TableCell className="text-slate-400 text-xs max-w-[200px]">
                        <EditableReason value={g.reason} onSave={(val) => updateReason(g.id, val)} />
                      </TableCell>
                      <TableCell className="text-slate-400 text-xs">{g.granted_at ? formatDate(g.granted_at) : '-'}</TableCell>
                      <TableCell className="text-sm">
                        {g.expires_at ? (
                          <span className="flex items-center gap-1 text-amber-400"><Clock className="w-3 h-3" />{formatDate(g.expires_at)}</span>
                        ) : (
                          <span className="text-emerald-500 text-xs">Lifetime</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={statusColors[g.status] || 'bg-slate-500/20 text-slate-300'}>
                          {g.status.charAt(0).toUpperCase() + g.status.slice(1)}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
